import React, { useState } from 'react';
import styled from 'styled-components';
import { motion } from 'framer-motion';
import { Lock } from 'lucide-react';
import { colors } from '../styles/colors';
import { typography } from '../styles/typography';
import { spacing } from '../styles/spacing';
import { authService } from '../services/AuthService';

const AuthContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  min-height: 100vh;
  padding: ${spacing[4]};
  background-color: ${colors.background.secondary};
`;

const AuthCard = styled.div`
  width: 100%;
  max-width: 400px;
  background: ${colors.background.primary};
  border-radius: ${spacing.radius['3xl']};
  padding: ${spacing[8]};
  box-shadow: ${colors.shadows.xl};
  text-align: center;
`;

const Title = styled.h1`
  font-size: ${typography.sizes['3xl']};
  font-weight: ${typography.weights.bold};
  color: ${colors.text.primary};
  margin-bottom: ${spacing[2]};
`;

const Subtitle = styled.p`
  color: ${colors.text.secondary};
  margin-bottom: ${spacing[8]};
`;

const InputWrapper = styled.div`
  position: relative;
  width: 100%;
  margin-bottom: ${spacing[4]};
`;

const StyledInput = styled.input`
  width: 100%;
  height: 56px;
  padding: 0 ${spacing[4]} 0 ${spacing[12]};
  border: 1px solid ${colors.border.primary};
  border-radius: ${spacing.radius.lg};
  background: ${colors.background.secondary};
  font-size: ${typography.sizes.base};
  color: ${colors.text.primary};
  transition: border-color 0.2s ease, box-shadow 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${colors.primary.DEFAULT};
    box-shadow: 0 0 0 3px ${colors.primary.light}60;
  }
`;

const InputIcon = styled.div`
  position: absolute;
  top: 50%;
  left: ${spacing[4]};
  transform: translateY(-50%);
  color: ${colors.text.muted};
  pointer-events: none;
`;

const SubmitButton = styled.button`
  width: 100%;
  height: 56px;
  background: ${colors.gradients.primary};
  color: ${colors.text.white};
  border: none;
  border-radius: ${spacing.radius.lg};
  font-size: ${typography.sizes.base};
  font-weight: ${typography.weights.semibold};
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease;

  &:hover {
    box-shadow: ${colors.shadows.lg};
    transform: translateY(-2px);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.p`
  color: ${colors.status.error};
  font-size: ${typography.sizes.sm};
  margin-top: ${spacing[4]};
`;

const AuthModal: React.FC = () => {
  const [passkey, setPasskey] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);

  const handleLogin = async () => {
    setIsLoading(true);
    setError('');

    const success = await authService.login(passkey);

    if (!success) {
      setError('Invalid passkey. Please try again.');
    }
    // On success, the App component will automatically re-render
    // and this modal will be unmounted.
    setIsLoading(false);
  };

  return (
    <AuthContainer
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <AuthCard>
        <Title>Love Maze</Title>
        <Subtitle>Please enter your passkey to continue.</Subtitle>

        <InputWrapper>
          <InputIcon>
            <Lock size={20} />
          </InputIcon>
          <StyledInput 
            type="password"
            placeholder="Passkey"
            value={passkey}
            onChange={(e) => setPasskey(e.target.value)}
            onKeyPress={(e) => e.key === 'Enter' && handleLogin()}
          />
        </InputWrapper>

        <SubmitButton onClick={handleLogin} disabled={isLoading || !passkey}>
          {isLoading ? 'Unlocking...' : 'Enter'}
        </SubmitButton>

        {error && <ErrorMessage>{error}</ErrorMessage>}
      </AuthCard>
    </AuthContainer>
  );
};

export default AuthModal;